import { useState } from 'react';
import { Button } from '@/ui/Button';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { cn } from '@/lib/cn';
import { FichaDePersona } from './FichaDePersona';
import { FotosMasivas } from './FotosMasivas';
import { FilaDePersona, TarjetaDePersona } from './piezas';
import { documento, descargar } from './csv';
import { useDirectorio, usePedirAreas, type Persona } from './api';

/**
 * Directorio.tsx — la pantalla de `#directorio`.
 *
 * ## Dos entradas que no son su enlace del menú
 *
 * Y ninguna estaba escrita en ninguna parte antes del 2026-09-11:
 *
 *   · `#directorio/<id>` — el clic en una mención `@compañero`. El shell ya
 *     tenía el id en `data-uid` y **lo tiraba**: `navigate('directory')` a
 *     secas. Pulsabas `@María` y acababas en la lista de trescientas personas,
 *     buscándola a mano, con su ficha a un dato de distancia.
 *
 *   · `#directorio/fotos` — el botón «Fotos masivas» de Administración, que
 *     antes llamaba a `PageDirectory.openBulkPhotos()` desde otro módulo.
 *
 * Las dos usan el mecanismo de parámetros de ruta que ya existía
 * (`window._routeParams`), el mismo con el que Perfil abre su ficha.
 *
 * ## La vista deja de perderse al buscar
 *
 * `loadDirectory` terminaba siempre en `renderUsers('grid')` y los chips no se
 * enteraban: estabas en «Lista», escribías una letra, volvían las tarjetas y el
 * chip seguía diciendo «Lista». Comprobado en el navegador. Aquí la vista es
 * estado y la búsqueda es otra cosa; no se pisan.
 */

type Vista = 'tarjetas' | 'lista';

export function Directorio() {
  const puedeGestionarFotos = Auth.isStaff();

  // Los parámetros de ruta se leen UNA vez, al montar: si se leyeran en cada
  // render, cerrar la ficha volvería a abrirla en el siguiente repintado.
  const [fichaDe, setFichaDe] = useState<number | null>(() => {
    const p = window._routeParams?.[0];
    return p && /^\d+$/.test(p) ? Number(p) : null;
  });
  const [fotos, setFotos] = useState(() => window._routeParams?.[0] === 'fotos');

  const [busqueda, setBusqueda] = useState('');
  const [vista, setVista] = useState<Vista>('tarjetas');

  const { data, isLoading, isError, error, refetch, isPlaceholderData } = useDirectorio(busqueda);
  const personas = data?.items ?? [];
  const total = data?.total ?? 0;
  const hayMas = total > personas.length;

  const pedirAreas = usePedirAreas();
  const [exportando, setExportando] = useState(false);

  async function exportar() {
    if (!personas.length) { Utils.toast('Carga el directorio primero', 'warning'); return; }
    setExportando(true);
    // Las áreas se piden aquí, no al montar: son lo único que las necesita, y
    // así el Directorio sigue haciendo UNA petición al abrirse.
    let porId = new Map<number, string>();
    try {
      porId = new Map((await pedirAreas()).map((a) => [a.id, a.name]));
    } catch {
      // El CSV sale igual, con la columna «Área» vacía. Perder los nombres de
      // área no puede costar la exportación entera — que es justo lo que hacía
      // el `Promise.all` de la ficha, y por lo mismo.
      Utils.toast('No se pudieron leer las áreas; el resto del archivo sale igual', 'warning');
    } finally {
      setExportando(false);
    }
    descargar(
      `directorio_suservicio_${new Date().toISOString().slice(0, 10)}.csv`,
      documento(
        ['Nombre', 'Cargo', 'Área', 'Ciudad', 'Email', 'Teléfono'],
        personas.map((p) => [
          p.full_name,
          p.position ?? '',
          (p.area_id != null ? porId.get(p.area_id) : '') ?? '',
          p.city ?? '',
          p.email ?? '',
          p.phone ?? '',
        ]),
      ),
    );
    // Se dice cuántas de cuántas. El vanilla decía «100 contactos exportados» y
    // callaba que había doscientos más.
    Utils.toast(
      hayMas
        ? `${personas.length} de ${total} contactos exportados`
        : `${personas.length} contactos exportados`,
      'success',
    );
  }

  return (
    <>
      <header className="page-header">
        <div>
          <h1 className="page-title">
            <i className="bi bi-people-fill" aria-hidden="true" /> Directorio
          </h1>
          <p className="page-subtitle">Encuentra y conecta con tus compañeros</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {puedeGestionarFotos && (
            <Button variant="secondary" size="sm" onClick={() => setFotos(true)}>
              <i className="bi bi-images" aria-hidden="true" /> Fotos masivas
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={exportar}
            disabled={exportando}
          >
            <i className="bi bi-download" aria-hidden="true" />
            {exportando ? 'Preparando…' : 'Exportar contactos'}
          </Button>
        </div>
      </header>

      <div className="mb-6 flex flex-wrap items-center gap-4">
        <div className="search-bar min-w-60 flex-1">
          <i className="bi bi-search search-bar-icon" aria-hidden="true" />
          <input
            type="text"
            className="search-input"
            aria-label="Buscar en el directorio"
            placeholder="Buscar por nombre, cargo, ciudad…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </div>

        {/* Botones, no `<span class="chip">`. Los dos selectores del vanilla no
            recibían foco, así que quien navegaba con teclado no podía ni siquiera
            cambiar a la vista de lista — la única en la que las fichas se podían
            abrir. `radiogroup` porque son excluyentes. */}
        <div className="filter-chips" role="radiogroup" aria-label="Cómo se ve el directorio">
          {([['tarjetas', 'bi-grid-3x3-gap', 'Tarjetas'],
             ['lista', 'bi-list-ul', 'Lista']] as const).map(([v, icono, etiqueta]) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={vista === v}
              className={cn('chip', vista === v && 'active')}
              onClick={() => setVista(v)}
            >
              <i className={`bi ${icono}`} aria-hidden="true" /> {etiqueta}
            </button>
          ))}
        </div>
      </div>

      {isLoading && (
        <div className="grid grid-auto-sm">
          {Array.from({ length: 12 }, (_, i) => <Skeleton key={i} className="h-44" />)}
        </div>
      )}

      {isError && (
        <EstadoError err={error} que="el directorio" onReintentar={() => { refetch(); }} />
      )}

      {!isLoading && !isError && !personas.length && (
        <EstadoVacio
          icono="bi bi-people"
          titulo={busqueda.trim() ? 'Sin colaboradores para esa búsqueda' : 'Sin colaboradores'}
          descripcion={busqueda.trim() ? 'Prueba con parte del nombre, el cargo o la ciudad.' : undefined}
        />
      )}

      {!!personas.length && (
        <>
          {/* `aria-live` para que quien usa lector de pantalla se entere de
              cuántos resultados hay al escribir, sin tener que recorrer la
              rejilla entera para contarlos. */}
          <p className="mb-3 text-2 text-muted" aria-live="polite">
            {hayMas
              ? `Mostrando ${personas.length} de ${total} colaboradores. Afina la búsqueda para ver otros.`
              : `${total} colaborador${total === 1 ? '' : 'es'}`}
            {isPlaceholderData && ' · actualizando…'}
          </p>

          {vista === 'tarjetas' ? (
            <div className="grid grid-auto-sm">
              {personas.map((p: Persona) => (
                <TarjetaDePersona key={p.id} p={p} alAbrir={setFichaDe} />
              ))}
            </div>
          ) : (
            <div className="card table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Colaborador</th><th>Cargo</th><th>Ciudad</th>
                    <th><i className="bi bi-envelope" aria-hidden="true" /> Email</th>
                    <th><i className="bi bi-telephone" aria-hidden="true" /> Teléfono</th>
                    <th><span className="sr-only">Ficha</span></th>
                  </tr>
                </thead>
                <tbody>
                  {personas.map((p: Persona) => (
                    <FilaDePersona key={p.id} p={p} alAbrir={setFichaDe} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <FichaDePersona
        id={fichaDe}
        alCerrar={() => setFichaDe(null)}
        puedeCambiarFoto={puedeGestionarFotos}
      />
      <FotosMasivas abierto={fotos} alCerrar={() => setFotos(false)} />
    </>
  );
}
